window.ESScrollLock = window.ESScrollLock || {
  locks: new Set(),
  requestLock(id) {
    this.locks.add(id);
    document.documentElement.setAttribute('data-scroll-locked', 'true');
  },
  releaseLock(id) {
    this.locks.delete(id);
    if (this.locks.size === 0) {
      document.documentElement.removeAttribute('data-scroll-locked');
    }
  }
};

class ESDrawer extends HTMLElement {
  constructor() {
    super();
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.close = this.close.bind(this);
    this.closeButton = null;
    this.overlay = null;
  }

  /**
   * The close and overlay listeners used to be attached in the constructor, which runs before this
   * element's children are guaranteed to exist. After a Theme Editor section re-render both queries
   * returned null and the drawer could only be dismissed with Escape - silently, with no error.
   */
  connectedCallback() {
    this.setAttribute('tabindex', '-1');
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    this.setAttribute('aria-hidden', 'true');

    this.closeButton = this.querySelector('.drawer__close');
    this.overlay = this.querySelector('.drawer__overlay');

    this.closeButton?.addEventListener('click', this.close);
    this.overlay?.addEventListener('click', this.close);

    this.addEventListener('keyup', this.onKeyUp);
    this.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    this.closeButton?.removeEventListener('click', this.close);
    this.overlay?.removeEventListener('click', this.close);
    this.closeButton = null;
    this.overlay = null;

    this.removeEventListener('keyup', this.onKeyUp);
    this.removeEventListener('keydown', this.onKeyDown);

    if (this.hasAttribute('open')) {
      window.ESScrollLock.releaseLock(this.id || 'drawer');
    }
  }

  open(opener) {
    this.openedBy = opener;
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    
    if (this.openedBy) {
      this.openedBy.setAttribute('aria-expanded', 'true');
    }

    window.ESScrollLock.requestLock(this.id || 'drawer');
    
    setTimeout(() => {
      this.trapFocus();
    }, 50);
  }

  close() {
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    
    if (this.openedBy) {
      this.openedBy.setAttribute('aria-expanded', 'false');
    }

    window.ESScrollLock.releaseLock(this.id || 'drawer');
    
    if (this.openedBy && document.body.contains(this.openedBy)) {
      this.openedBy.focus();
    }
  }

  onKeyUp(evt) {
    if (evt.code === 'Escape') this.close();
  }

  onKeyDown(e) {
    const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
    if (!isTabPressed || !this.hasAttribute('open')) return;

    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  }

  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    } else {
      this.focus();
    }
  }
}

if (!customElements.get('es-drawer')) {
  customElements.define('es-drawer', ESDrawer);
}
